/**
 * Catálogo de tipos e suas props.
 *
 * O modelo antigo tinha `BuilderComponentType` (união de 23 strings) e
 * `BuilderProps` (união de 22 interfaces) sem discriminante: dado um
 * `BuilderNode`, o compilador não sabia qual props era qual, e o código todo
 * caía em `(node.props as any)?.x` — 525 anotações `any` só neste módulo.
 *
 * Aqui o vínculo tipo -> props é um MAPA. `switch (node.type)` estreita
 * `node.props` sozinho, e um tipo novo é uma linha aqui mais um descriptor.
 *
 * `props` guarda só o que é do widget: rótulo, dica, validação estática, opções.
 * Posição está em LayoutSpec, dados em BindingSpec, lógica em BehaviorSpec.
 */
import { LayoutSpec, StyleTokens } from './layout';
import { BindingSpec } from './binding';
import { BehaviorSpec } from './behavior';
import { AttributeFieldMap, AttributeNesting, AttributePersistence } from './attribute-schema';
import { DashboardCardKind, DashboardCardSource, DashboardFilterBinding } from './dashboard-schema';
import type { MiniCardInterface } from '../../system-view/main-dashboard/interfaces/minicard';
import type { ChartsInterface } from '../../system-view/main-dashboard/interfaces/charts';
import type { TableInterface } from '../../system-view/main-dashboard/interfaces/table';

// ---------------------------------------------------------------- base

/** Comum a todo widget de formulário. */
export interface FieldPropsBase {
  /** Chave de tradução do rótulo. */
  labelKey?: string;
  /** Chave de tradução da dica dentro do campo. */
  placeholderKey?: string;
  /** Chave de tradução do texto de ajuda. */
  helpKey?: string;
  /** Obrigatoriedade estática. A condicional vive em `behavior.requiredWhen`. */
  required?: boolean;
  /** Somente leitura estático. */
  readonly?: boolean;
  /** Oculto de forma estática. A visibilidade condicional vive em `behavior.visibleWhen`. */
  hidden?: boolean;
  defaultValue?: unknown;
}

/** Comum a todo container. */
export interface ContainerPropsBase {
  labelKey?: string;
}

// ---------------------------------------------------------------- containers

export interface ScreenProps {
  /** Título da tela. */
  titleKey?: string;
}

export interface TabsProps extends ContainerPropsBase {
  /**
   * Campos exibidos na barra de contexto (topo das abas).
   * Só referências: o dado sai do item selecionado em runtime.
   */
  contextFields?: readonly { labelKey: string; dataPath: string }[];
}

export interface TabProps {
  /** Chave de tradução do título da aba. */
  labelKey: string;
  icon?: string;
  iconTheme?: 'outline' | 'fill' | 'twotone';
  disabled?: boolean;
}

export interface PanelProps extends ContainerPropsBase {
  /** Chave de tradução do nome do painel. */
  nameKey: string;
  active?: boolean;
  disabled?: boolean;
  collapsible?: boolean;
  /** Abre recolhido. */
  collapsed?: boolean;
}

export interface RowProps {
  /** Espaçamento entre os campos, por token. */
  gap?: 'none' | 'small' | 'medium' | 'normal' | 'large' | 'extraLarge';
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  justifyContent?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
}

export interface SectionProps extends ContainerPropsBase {
  titleKey?: string;
  collapsible?: boolean;
  collapsed?: boolean;
}

// ---------------------------------------------------------------- campos de texto

export interface TextProps extends FieldPropsBase {
  minLength?: number;
  maxLength?: number;
  /** Expressão regular de validação. */
  pattern?: string;
  /** Máscara de entrada (ex.: CPF, CNPJ, telefone). */
  mask?: string;
  defaultValue?: string;
}

export interface TextAreaProps extends FieldPropsBase {
  minRows?: number;
  maxLength?: number;
  defaultValue?: string;
}

// ---------------------------------------------------------------- número e data

export interface NumberProps extends FieldPropsBase {
  min?: number;
  max?: number;
  step?: number;
  /** Casas decimais aceitas. */
  precision?: number;
  /** Casas decimais exibidas. */
  decimalPlaces?: number;
  formatThousands?: boolean;
  thousandSeparator?: '.' | ',';
  decimalSeparator?: '.' | ',';
  /** Prefixo e sufixo fixos (ex.: "R$", "%"). */
  addonBefore?: string;
  addonAfter?: string;
  defaultValue?: number;
}

export interface DateProps extends FieldPropsBase {
  /** Formato de exibição (ex.: "dd/MM/yyyy"). */
  format?: string;
  showTime?: boolean;
  defaultValue?: string;
}

// ---------------------------------------------------------------- booleano

export interface SwitchProps extends FieldPropsBase {
  /** Textos curtos dentro do controle. */
  checkedLabelKey?: string;
  uncheckedLabelKey?: string;
  defaultValue?: boolean;
}

// ---------------------------------------------------------------- seleção

export interface SelectOption {
  readonly value: string | number | boolean;
  /** Chave de tradução, ou texto literal quando vem de dados. */
  readonly labelKey: string;
  readonly isDefault?: boolean;
}

export interface SelectProps extends FieldPropsBase {
  options?: readonly SelectOption[];
  multiple?: boolean;
  showSearch?: boolean;
}

/** Lista suspensa com opções configuráveis. Mantido separado de `select` porque
 *  o modelo antigo tratava os dois como tipos distintos e há telas gravadas assim. */
export interface DropdownProps extends FieldPropsBase {
  options?: readonly SelectOption[];
  multiple?: boolean;
  showSearch?: boolean;
}

/** Opções vindas de consulta a uma entidade. A consulta está em `binding.query`. */
export interface QuerySelectProps extends FieldPropsBase {
  multiple?: boolean;
  showSearch?: boolean;
  /** Exibir a chave junto da descrição. */
  showKey?: boolean;
}

/** Busca em cadastro. A entidade e os campos estão em `binding.query`. */
export interface SearchProps extends FieldPropsBase {
  /** Exibir a chave junto da descrição. */
  showKey?: boolean;
}

export interface TagProps extends FieldPropsBase {
  options?: readonly SelectOption[];
}

// ---------------------------------------------------------------- chave/valor

export interface KeyValueProps extends FieldPropsBase {
  autocompleteKeys?: readonly string[];
  autocompleteValues?: readonly string[];
}

export interface KeyValueObjectProps extends FieldPropsBase {
  /** Objeto de chaves definidas em tempo de uso. */
  keyLabelKey?: string;
  valueLabelKey?: string;
}

// ---------------------------------------------------------------- estáticos

export interface DividerProps {
  /**
   * O traco da linha.
   *
   * Substitui o `dashed` booleano, que so sabia dizer "tracejado ou nao".
   */
  variant?: 'solid' | 'dashed' | 'dotted';
  /**
   * O campo antigo, booleano.
   *
   * Continua lido porque as telas ja gravadas so tem ele: ignora-lo mudaria a
   * aparencia de tela publicada. Quando `variant` esta escolhido, ele perde.
   */
  dashed?: boolean;
  /**
   * O PAPEL da cor, e nao a cor.
   *
   * Nao ha hexadecimal aqui de proposito: quem escolhe o tom e o tema, e e isso
   * que faz o divisor continuar legivel nos oito temas mais o personalizado.
   */
  color?: 'default' | 'muted' | 'strong' | 'accent' | 'success' | 'warning' | 'danger';
  orientation?: 'horizontal' | 'vertical';
  titleAlign?: 'left' | 'center' | 'right';
  titleKey?: string;
}

export interface ImageProps {
  /** URL fixa. A dinâmica vem de `binding.dataPath`. */
  url?: string;
  /** Texto alternativo, para leitor de tela. */
  altKey?: string;
  width?: number;
  height?: number;
  /** Ampliar ao clicar. */
  enableLightbox?: boolean;
  tooltipKey?: string;
}

export interface ButtonProps {
  labelKey?: string;
  variant?: 'primary' | 'default' | 'dashed' | 'text' | 'link' | 'danger';
  size?: 'small' | 'default' | 'large';
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  /**
   * O que o botão faz. Ações compostas ficam em `behavior.events` com
   * `event: 'onClick'` — este campo cobre o caso simples.
   */
  action?: 'submit' | 'cancel' | 'callFlow' | 'custom';
  /** Configuração do fluxo, quando `action: 'callFlow'`. */
  flow?: {
    /** Identificador da configuração (MKTPCFG). */
    configId?: number;
    configName?: string;
    /** Expor o seletor de empresa. */
    showCompanySelector?: boolean;
    companyCode?: number;
    /** Motor novo (assíncrono) em vez do antigo. */
    newEngine?: boolean;
    /** O que enviar: a tela toda ou apenas o item selecionado. */
    dataMode?: 'full' | 'selectedItem';
  };

  /**
   * Registro da execução do botão.
   *
   * Hoje o usuário clica em "executar fluxo" e não tem como saber o que
   * aconteceu: se o fluxo rodou, o que foi enviado, o que voltou, quanto tempo
   * levou, se falhou e por quê. O `console.log` do desenvolvedor não serve para
   * quem opera a tela.
   */
  log?: {
    /** Guardar o histórico de execuções deste botão. */
    enabled: boolean;
    /** Mostrar o painel de histórico ao lado do botão. */
    showPanel?: boolean;
    /** Guardar o JSON enviado. Cuidado: pode conter dado sensível. */
    capturePayload?: boolean;
    /** Guardar a resposta do fluxo. */
    captureResponse?: boolean;
    /** Quantas execuções manter em memória. */
    keepLast?: number;
    /** Gravar também no banco, para auditoria além da sessão. */
    persist?: boolean;
  };
}

/** Uma execução registrada de botão de ação. */
export interface ActionExecutionEntry {
  readonly id: string;
  /** Id do nó do botão. */
  readonly buttonId: string;
  /** Momento do disparo, em ISO. */
  readonly startedAt: string;
  /** Duração em ms. Ausente enquanto está rodando. */
  readonly durationMs?: number;
  readonly status: 'running' | 'success' | 'error' | 'cancelled';
  /** Identificador da configuração de fluxo acionada. */
  readonly flowConfigId?: number;
  readonly flowConfigName?: string;
  /** JSON enviado, quando `capturePayload`. */
  readonly payload?: unknown;
  /** Resposta recebida, quando `captureResponse`. */
  readonly response?: unknown;
  /** Mensagem de erro, quando `status: 'error'`. */
  readonly errorMessage?: string;
  /** Passos reportados pelo fluxo, quando ele informa progresso. */
  readonly steps?: readonly { readonly at: string; readonly messageKey: string }[];
}

// ---------------------------------------------------------------- arvore

/** Uma coluna exibida ao lado do rótulo do nó, na árvore. */
export interface TreeColumn {
  readonly field: string;
  readonly headerKey: string;
  readonly width?: string;
  readonly editable?: boolean;
}

/**
 * Árvore de itens.
 *
 * O dado de origem é hierárquico: cada item tem uma lista de filhos, apontada por
 * `childrenPath`. Serve para categoria com subcategoria, estrutura de produto,
 * menu, e para qualquer JSON que venha aninhado — casos que hoje só dão para
 * montar com componente Angular escrito à mão.
 */
export interface TreeViewProps extends ContainerPropsBase {
  /** Caminho, dentro do item, para a lista de filhos. Ex.: 'children', 'items'. */
  childrenPath: string;
  /** Caminho, dentro do item, para o texto exibido. */
  labelPath: string;
  /** Caminho, dentro do item, para o identificador. */
  idPath?: string;

  /** Caixa de marcação em cada nó. */
  selectable?: boolean;
  /** Marcar o pai marca os filhos. */
  cascadeSelection?: boolean;
  /** Permitir arrastar nós para reordenar ou trocar de pai. */
  draggable?: boolean;
  /** Campo de busca acima da árvore. */
  searchable?: boolean;

  /** Níveis expandidos ao abrir. 0 = só a raiz; -1 = todos. */
  expandToLevel?: number;
  /** Colunas extras à direita do rótulo. */
  columns?: readonly TreeColumn[];
}

// ---------------------------------------------------------------- atributos de marketplace

/**
 * Conjunto de atributos de uma categoria de marketplace.
 *
 * Substitui os cinco componentes que hoje resolvem o mesmo problema em ~1.570
 * linhas — Shopee, TikTok, B2W, ViaVarejo e Mercado Livre. O que varia entre eles
 * é declarado em `profileId` (perfil nativo) ou em `fields` (mapeamento próprio),
 * nunca em código novo.
 *
 * Com isso, a tela de atributos de um marketplace novo passa a ser arrastável.
 */
export interface AttributeSetProps extends ContainerPropsBase {
  /**
   * Perfil nativo. Ex.: 'shopee', 'tiktok', 'b2w', 'meli'.
   * Quando presente, `sourcePath`, `nesting` e `fields` saem do perfil, e o que
   * for declarado aqui sobrescreve ponto a ponto.
   */
  profileId?: string;

  /** Caminho, no JSON de origem, até o array de atributos. */
  sourcePath?: string;
  /** Plano, agrupado por um campo, ou árvore (filho pendurado no valor). */
  nesting?: AttributeNesting;
  /** Mapeamento: onde cada conceito está no JSON deste marketplace. */
  fields?: AttributeFieldMap;
  /** Onde gravar o que o usuário escolheu. */
  persistence?: AttributePersistence;

  /** Esconder atributos não obrigatórios por padrão. */
  onlyRequiredByDefault?: boolean;
  /** Campo de busca sobre os atributos. */
  searchable?: boolean;
  /** Colunas do grid de atributos, quando a exibição é tabular. */
  display?: 'cards' | 'list' | 'tree';
}

// ---------------------------------------------------------------- componente proprio

/**
 * Escape hatch: um componente Angular do projeto, dentro da tela montada.
 *
 * Existe porque nenhum catálogo cobre tudo. Quando o caso é específico demais
 * para ser declarado — um cálculo, uma integração, uma visualização própria — a
 * saída não pode ser "não dá": tem de ser plugar código de verdade.
 *
 * Duas formas, e a diferença entre elas é de confiança:
 *
 *  1. `componentKey` — componente JÁ COMPILADO no projeto, registrado num mapa
 *     de componentes permitidos. É a forma recomendada: passa por revisão de
 *     código, build e teste, como qualquer outro componente.
 *
 *  2. `source` — TypeScript, HTML e CSS anexados à tela e compilados em runtime.
 *     Isto NÃO é habilitado por padrão. Executar código vindo do banco foi
 *     exatamente o problema que acabamos de remover do modo expert
 *     (`new Function` com código de TEMPLATEJSON, alcançável pelo editor de JSON,
 *     num produto de público duplo). Se for necessário mesmo, precisa de:
 *     restrição a papéis internos, compilação no servidor e versionamento do
 *     fonte — não compilação no navegador do usuário.
 */
export interface CustomComponentProps extends ContainerPropsBase {
  /** Chave do componente no registro de componentes permitidos. */
  componentKey?: string;

  /** Entradas passadas ao componente. */
  inputs?: Readonly<Record<string, unknown>>;

  /**
   * Fonte anexado. Requer `allowAttachedSource` nas capacidades do editor, que
   * é desligado por padrão. Ver o comentário acima antes de habilitar.
   */
  source?: {
    readonly typescript?: string;
    readonly html?: string;
    readonly css?: string;
  };

  /** Altura reservada, para a tela não pular enquanto o componente carrega. */
  minHeight?: number;
}

// ---------------------------------------------------------------- dashboard

/**
 * Um cartão de dashboard dentro da tela: indicador, gráfico ou tabela.
 *
 * O nó guarda a configuração no MESMO formato que o dashboard persiste em
 * `Mktpdashboard.EXPRESSION` (`MiniCardInterface`, `ChartsInterface`,
 * `TableInterface`), e não numa cópia. Assim um cartão configurado no dashboard
 * pode ser colado numa tela, e vice-versa, sem conversão nem perda — e sem
 * criar um segundo contrato para o mesmo JSON.
 *
 * O que a tela acrescenta ao cartão é o que o dashboard não tem: os campos do
 * formulário da tela alimentam os parâmetros da query, por `filters`. Ver
 * `DashboardFilterBinding`.
 */
export interface DashboardCardProps extends ContainerPropsBase {
  /** Qual dos três cartões. Decide qual das configurações abaixo vale. */
  kind: DashboardCardKind;

  /** Configuração do cartão, no formato do dashboard. Só a de `kind` é lida. */
  minicard?: MiniCardInterface;
  chart?: ChartsInterface;
  table?: TableInterface;

  /** Query própria, ou dado que já está na tela. */
  source?: DashboardCardSource;
  /**
   * Caminho no JSON da tela, quando `source: 'dataPath'`. O cartão recebe as
   * linhas prontas e não vai ao banco.
   */
  dataPath?: string;

  /** Campos da tela que alimentam os `@PARAM` da query do cartão. */
  filters?: readonly DashboardFilterBinding[];

  /**
   * Segundos entre recargas automáticas. Herda o `refresh` da configuração do
   * cartão quando ausente. Zero ou ausente desliga.
   *
   * O cartão só recarrega com a tela em foco (`DashboardFocusService`), o que
   * evita o caso ruim de dez telas abertas consultando o banco em paralelo.
   */
  refreshSeconds?: number;

  /** Altura reservada, para a tela não pular enquanto o cartão carrega. */
  minHeight?: number;
}

/**
 * Um dashboard INTEIRO embutido na tela, por id.
 *
 * Diferente do `dashboardCard`: aqui vem tudo — as abas do dashboard, o
 * gridster, a barra de filtro própria. É a saída para "quero esta tela com o
 * dashboard de vendas embaixo", sem remontar cartão por cartão.
 *
 * Já funciona hoje de forma imperativa, via
 * `<component-factory [component]="'MainDashboardComponent'" [inputs]="...">`
 * — `MainDashboardComponent` está no `REGISTRY` do `DynamicLoaderService` e
 * aceita `@Input() iddashboard`. O que este tipo faz é tornar isso declarativo,
 * arrastável e versionado junto com a tela.
 *
 * Contrapartida honesta: o dashboard traz as próprias abas e o próprio filtro,
 * então é uma caixa com regras de navegação próprias dentro da tela. Para
 * misturar indicador com campo de formulário, `dashboardCard` é o certo.
 */
export interface DashboardEmbedProps extends ContainerPropsBase {
  /** Id do dashboard em `Mktpdashboard`. */
  dashboardId?: number;

  /** Mostrar a barra de filtro do próprio dashboard. */
  showFilter?: boolean;
  /** Mostrar a barra de título e ações das abas. */
  showToolbar?: boolean;

  /** Abrir direto numa aba específica, pelo `tabName`. */
  initialTabName?: string;

  /** Campos da tela que alimentam os filtros do dashboard. */
  filters?: readonly DashboardFilterBinding[];

  /** Altura reservada. Dashboard sem altura definida colapsa. */
  minHeight?: number;
}

// ---------------------------------------------------------------- tabelas e grade

export type ColumnType = 'text' | 'number' | 'date' | 'boolean' | 'search' | 'dropdown';

export interface GridColumn {
  readonly field: string;
  readonly headerKey: string;
  readonly type: ColumnType;
  /** Caminho do valor dentro do item da lista. */
  readonly dataPath: string;
  readonly width?: string;
  readonly sortable?: boolean;
  readonly filterable?: boolean;
  readonly editable?: boolean;
  readonly formatter?: string;
  /** Coluna do tipo `dropdown`. */
  readonly options?: readonly SelectOption[];
  readonly multiple?: boolean;
  readonly showSearch?: boolean;
  /** Coluna do tipo `search`: consulta e propagação. */
  readonly query?: import('./binding').EntityQuery;
  readonly showKey?: boolean;
  readonly mappings?: readonly import('./binding').FieldMapping[];
}

/**
 * Planilha.
 *
 * O tipo permanece no catálogo porque `<app-builder-spreadsheet>` está vivo no
 * canvas (duas ocorrências) e o tipo é oferecido na paleta — pode haver telas
 * gravadas com ele. Sem as fixtures de produção não há como afirmar o contrário,
 * e migração sem perda vem antes de simplificar o catálogo.
 *
 * Se a apuração das fixtures mostrar zero uso, o tipo sai num bump de schema, e o
 * modo planilha continua disponível como `viewMode` da grade de dados — que é
 * onde ele de fato funciona.
 */
export interface SpreadsheetProps extends ContainerPropsBase {
  showToolbar?: boolean;
  showFormulaBar?: boolean;
  readonly?: boolean;
  maxRows?: number;
  maxColumns?: number;
  defaultRowHeight?: number;
  defaultColumnWidth?: number;
  allowImportExport?: boolean;
  enableFormulas?: boolean;
  /**
   * Conteúdo da planilha. Mantido opaco: a estrutura antiga tinha seis interfaces
   * aninhadas (folhas, células, estilos, bordas, mesclagens, validação) e nenhum
   * consumidor além do próprio componente. O migrador carrega como está.
   */
  data?: unknown;
}

/** Lista editável simples, sem paginação. */
export interface SimpleTableProps extends ContainerPropsBase {
  columns?: readonly GridColumn[];
  allowAdd?: boolean;
  allowRemove?: boolean;
}

/** Tabela com ordenação e filtro, sem CRUD nem mestre-detalhe. */
export interface TableProps extends ContainerPropsBase {
  columns?: readonly GridColumn[];
  sortable?: boolean;
  filterable?: boolean;
  paginated?: boolean;
}

export interface DataGridProps extends ContainerPropsBase {
  columns: readonly GridColumn[];
  paginated?: boolean;
  pageSize?: number;
  /** Linhas visíveis antes do scroll. 0 = sem limite. */
  maxVisibleRows?: number;
  filterable?: boolean;
  sortable?: boolean;
  /** Permitir incluir, editar e excluir. */
  enableCrud?: boolean;
  selectionMode?: 'none' | 'single' | 'multiple';

  /** Como o formulário aparece em relação à grade. */
  viewMode?: 'inline' | 'drawer' | 'flip' | 'spreadsheet';
  /** Largura da gaveta, em % da tela. Usado quando `viewMode: 'drawer'`. */
  drawerWidth?: number;

  /** Mestre-detalhe: painéis que recebem os campos da linha escolhida. */
  masterDetail?: {
    enabled: boolean;
    /** Ids dos painéis conectados. */
    panelIds: readonly string[];
    autoGenerateFields?: boolean;
  };

  /** Cria a primeira linha quando a tela abre sem dados. */
  autoAddFirstRow?: boolean;
  /** Ordem, quando mais de uma grade faz auto-add. */
  autoAddOrder?: number;
}

// ---------------------------------------------------------------- o mapa

/**
 * Único ponto de extensão do catálogo. Uma linha aqui e um descriptor no registry.
 *
 * Os 23 tipos do modelo antigo continuam todos aqui: nenhum sai do catálogo antes
 * das fixtures de produção dizerem o que está em uso.
 */
export interface NodePropsMap {
  // containers
  screen: ScreenProps;
  tabs: TabsProps;
  tab: TabProps;
  panel: PanelProps;
  row: RowProps;
  section: SectionProps;
  // campos
  text: TextProps;
  textarea: TextAreaProps;
  number: NumberProps;
  date: DateProps;
  switch: SwitchProps;
  select: SelectProps;
  dropdown: DropdownProps;
  querySelect: QuerySelectProps;
  search: SearchProps;
  tag: TagProps;
  keyValue: KeyValueProps;
  keyValueObject: KeyValueObjectProps;
  // estáticos e ação
  divider: DividerProps;
  image: ImageProps;
  button: ButtonProps;
  // dashboard
  dashboardCard: DashboardCardProps;
  dashboardEmbed: DashboardEmbedProps;
  // hierarquico e extensao
  treeview: TreeViewProps;
  attributeSet: AttributeSetProps;
  customComponent: CustomComponentProps;
  // tabulares
  spreadsheet: SpreadsheetProps;
  simpletable: SimpleTableProps;
  table: TableProps;
  datagrid: DataGridProps;
}

export type NodeType = keyof NodePropsMap;

/** Todos os tipos, em runtime — útil para validação e para o registry. */
export const NODE_TYPES = [
  'screen', 'tabs', 'tab', 'panel', 'row', 'section',
  'text', 'textarea', 'number', 'date', 'switch',
  'select', 'dropdown', 'querySelect', 'search', 'tag',
  'keyValue', 'keyValueObject',
  'divider', 'image', 'button',
  'dashboardCard', 'dashboardEmbed',
  'treeview', 'attributeSet', 'customComponent',
  'spreadsheet', 'simpletable', 'table', 'datagrid'
] as const satisfies readonly NodeType[];

export function isNodeType(value: string): value is NodeType {
  return (NODE_TYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------- o nó

export interface NodeBase<K extends NodeType = NodeType> {
  readonly id: string;
  readonly type: K;
  readonly props: NodePropsMap[K];
  readonly layout: LayoutSpec;
  readonly style?: StyleTokens;
  readonly binding?: BindingSpec;
  readonly behavior?: BehaviorSpec;
  readonly children?: readonly ScreenNode[];
}

/**
 * União distributiva: é ela que faz `switch (node.type)` estreitar `node.props`.
 * Sem o `K extends NodeType ? ... : never`, `props` viria como a união de todas.
 */
export type ScreenNode<K extends NodeType = NodeType> =
  K extends NodeType ? NodeBase<K> : never;

/** Nó de um tipo específico, para assinaturas de renderer e editor de propriedade. */
export type TypedNode<K extends NodeType> = NodeBase<K>;
